import { PaginationApiType } from '@shared/types/data-table.types';

/**
 * Job lifecycle statuses. Mirrors the `model_jobs` row lifecycle described in
 * the platform Job API contract (Jira SPYG-612): PENDING → RUNNING → COMPLETED,
 * FAILED, or CANCELLED. FE polls list responses on-demand; there is no
 * client-side polling loop.
 */
export enum LookalikeJobStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export interface CreateLookalikeJob {
  jobId: string;
  resultAudienceId: string;
  seedAudienceId: string;
  jobName: string;
  status: LookalikeJobStatus;
  targetSize: number;
  createdAt: string;
  /** Optional backend toast/status text. */
  message?: string;
}

/**
 * A single lookalike job. Represents one seed → expansion invocation of the
 * lookalike-agent, plus the StiristaOne-side row that tracks its lifecycle.
 */
export interface LookalikeJob {
  jobId: string;
  jobName: string;
  seedAudienceId: string;
  seedAudienceTitle: string;
  status: LookalikeJobStatus;
  /** what the user asked for */
  targetSize: number | null;
  /** how many people the source audience had */
  seedSize: number;
  /** how many the engine produced. Does not change */
  expandedCount: number;
  /** how many the audience holds after the threshold. Different number from expandedCount */
  audienceSize: number;
  /** the threshold, null until set */
  /**
   * Audience cutoff from the BE on [-1, 1], encoded as a numeric
   * string. When present, the results slider is seeded from this value
   * instead of the client default of -1.
   */
  minAffinity: number | null;
  /** match quality, 0–100, for the whole audience */
  overallSimilarity: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  initiatedBy: string;
  /** Persisted engine failure reason when `status === FAILED`. */
  errorReason?: string | null;
  /** Present once the adapter has ingested results. */
  resultAudienceId?: string | null;
}

/**
 * Payload accepted by `POST tenants/{tenantId}/lookalike-jobs/start`.
 * FE supplies the seed audience id, user-chosen job name, and target size.
 */
export interface CreateLookalikeJobRequest {
  seedAudienceId: string;
  jobName: string;
  targetSize: number;
}

/**
 * Payload accepted by `POST tenants/{tenantId}/lookalike-jobs/{jobId}/save`.
 * Rename sends only `jobName`. Persisting the threshold sends only `affinity`.
 */
export interface SaveLookalikeAudienceRequest {
  jobName?: string;
  minAffinity?: number;
}

export interface SaveLookalikeAudienceResponse {
  audienceId: string;
  /** Optional backend toast/status text. */
  message?: string;
}

/** Body for `DELETE tenants/{tenantId}/lookalike-jobs/{jobId}`. */
export interface DeleteLookalikeJobResponse {
  /** Optional backend toast/status text. */
  message?: string;
}

/** Body for `POST tenants/{tenantId}/lookalike-jobs/{jobId}/cancel`. */
export interface CancelLookalikeJobResponse {
  /** Optional backend toast/status text. */
  message?: string;
}

/**
 * Reads a non-empty backend `message` from a success or error body.
 * @param payload - API success body or thrown error object.
 * @returns Trimmed message, or null when missing or blank.
 */
export function getLookalikeApiMessage(payload: unknown): string | null {
  if (payload === null || typeof payload !== 'object') {
    return null;
  }
  if (!('message' in payload)) {
    return null;
  }
  const message = payload.message;
  if (typeof message !== 'string') {
    return null;
  }
  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Minimum length of a user-chosen lookalike job name. */
export const LOOKALIKE_JOB_NAME_MIN_LENGTH = 3;

/** Minimum lookalike target size accepted by the create modal. */
export const LOOKALIKE_TARGET_SIZE_MIN = 1;

/** Minimum org-level lookalike generation sample size. */
export const LOOKALIKE_SAMPLE_SIZE_MIN = 1;

/**
 * Org-level lookalike generation settings from
 * `GET/PUT organizations/{organizationId}/lookalike-settings`.
 */
export interface LookalikeSettings {
  sampleSize: number;
  isDefault: boolean;
}

/** Payload accepted by `PUT organizations/{organizationId}/lookalike-settings`. */
export interface UpdateLookalikeSettingsRequest {
  sampleSize: number;
}

export interface GetLookalikeJobsResponse {
  jobs: LookalikeJob[];
  pagination: PaginationApiType;
}

/**
 * Per-ID row in the expanded output. `affinity` is the per-ID cosine to the
 * assigned cluster centroid on [-1, 1]. NOT the audience-level match score
 * (see `LookalikeValidation` for that distinct measure).
 *
 * `stiristaId` is INT64 in BQ (per the epic's normalization contract) and
 * fits safely in a JS `number` for the ID ranges we produce (well under
 * `Number.MAX_SAFE_INTEGER`). If the BE ever adopts a string envelope at the
 * API boundary to preserve full INT64 precision, widen this to
 * `number | string` and normalize in the API service.
 */
export interface LookalikeExpandedId {
  stiristaId: string;
  clusterId: number;
  affinity: number;
}

/**
 * Per-cluster metadata. `name` and `description` are LLM-generated by the
 * agent's cluster-naming step; may be empty when the naming step has not run.
 */
export interface LookalikeClusterMetadata {
  clusterId: number;
  name: string;
  description: string;
  seedCount: number;
  expandedCount: number;
}

/**
 * Validation artifact from the agent. `overallSimilarity` is the headline
 * "audience match quality" score on [0, 100] (seed-vs-expanded distribution
 * match). `perAttribute` is the same metric broken down by attribute. Do
 * NOT conflate with per-ID `affinity` in `LookalikeExpandedId`.
 */
export interface LookalikeValidation {
  overallSimilarity: number;
  perAttribute: Record<string, number>;
  /** Attributes the vendor's validator actually compared. If shorter than the
   * audience's full attribute set, callers should surface the caveat. */
  attributesCompared: string[];
}

/**
 * Bundled results returned by `GET tenants/{tenantId}/lookalike-jobs/{jobId}`.
 * Each field originates from a distinct agent output file (expanded.csv,
 * clusters.json, validation.json).
 */
export interface LookalikeResults {
  expandedSample: LookalikeExpandedId[];
  clusters: LookalikeClusterMetadata[];
  validation: LookalikeValidation;
}

/**
 * Type guard for lookalike result artifacts. Rejects a job-row body that
 * lacks `expandedSample`, `clusters`, and `validation`.
 * @param payload - Unknown HTTP body.
 * @returns True when the body has the results-view shape.
 */
export function isLookalikeResults(
  payload: unknown,
): payload is LookalikeResults {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const candidate = payload as Record<string, unknown>;
  const validation = candidate['validation'];
  return (
    Array.isArray(candidate['expandedSample']) &&
    Array.isArray(candidate['clusters']) &&
    validation !== null &&
    typeof validation === 'object'
  );
}

/**
 * Job plus optional artifacts from `GET tenants/{tenantId}/lookalike-jobs/{id}`.
 * The same response can be a job row, result artifacts, or both.
 */
export interface LookalikeDetails {
  job: LookalikeJob | null;
  results: LookalikeResults | null;
}

/**
 * True when a value is one of the lookalike job statuses.
 * @param value - Unknown status field.
 * @returns Whether the value is a `LookalikeJobStatus`.
 */
function isLookalikeJobStatus(value: unknown): value is LookalikeJobStatus {
  return (
    value === LookalikeJobStatus.PENDING ||
    value === LookalikeJobStatus.RUNNING ||
    value === LookalikeJobStatus.COMPLETED ||
    value === LookalikeJobStatus.FAILED ||
    value === LookalikeJobStatus.CANCELLED
  );
}

/**
 * Type guard for a lookalike job row. Result-only bodies fail this check.
 * @param payload - Unknown HTTP body.
 * @returns True when the body has a job id, name, and status.
 */
export function isLookalikeJob(payload: unknown): payload is LookalikeJob {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const job = payload as Partial<LookalikeJob>;
  return (
    typeof job.jobId === 'string' &&
    job.jobId.length > 0 &&
    typeof job.jobName === 'string' &&
    isLookalikeJobStatus(job.status)
  );
}
